import { useState, type FC } from "react"
import { useNavigate, useParams } from "react-router"

import { parseNumber } from "@forento/shared/utilities/number"

import { useAlert } from "~/contexts/AlertContext"
import { useUser } from "~/contexts/UserContext"
import Hero, { CallToActionButtons, CallToActionInfo } from "~/themes/original/components/Hero"
import Layout from "~/themes/original/components/Layout"
import PartialLoadingPage from "~/themes/original/components/PartialLoadingPage"
import PriceButton from "~/themes/original/pages/membership/MembershipPage/PriceButton"
import routes from "~/utilities/routes"
import trpc, { query } from "~/utilities/trpc"

const MembershipPage: FC = () => {
	const membershipId = parseNumber(useParams().membershipId)
	const user = useUser()
	const navigate = useNavigate()
	const alert = useAlert()

	const [submittingPriceId, setSubmittingPriceId] = useState<number>()

	const { data: membership, error } = query.membership.getPublic.useQuery(membershipId ?? -1)

	async function handleSelect(priceId: number) {
		if (!user.user) {
			return navigate(`${routes.user.signup()}?next=${encodeURIComponent(location.pathname)}`)
		}

		setSubmittingPriceId(priceId)
		try {
			const result = await trpc.payment.joinMembershipPrice.mutate(priceId)
			switch (result.status) {
				case "success":
					await user.reload()
					setSubmittingPriceId(undefined)
					break
				case "redirect":
					window.location.assign(result.url)
					break
			}
		} catch (error) {
			console.error(error)
			await alert.show("Error", "Failed to join membership. Please try again later.")
			setSubmittingPriceId(undefined)
		}
	}

	async function handleCancel() {
		const membership = user.user!.membership
		if (membership === null || membership.type === "external") return

		setSubmittingPriceId(membership.priceId)
		try {
			await trpc.payment.leaveMembership.mutate()
			await user.reload()
		} finally {
			setSubmittingPriceId(undefined)
		}
	}

	return (
		<Layout>
			{error ? (
				<p>Failed to load membership.</p>
			) : !membership ? (
				<PartialLoadingPage />
			) : (
				<Hero
					title={membership.title}
					thumbnailUrl={membership.thumbnailFilePath}
					htmlDescription={membership.longDescription?.html ?? null}
					callToAction={
						user.user?.isCreator ? (
							<CallToActionInfo>You cannot subscribe to a membership as a creator.</CallToActionInfo>
						) : (
							<CallToActionButtons>
								{membership.prices.map(price => (
									<PriceButton
										key={price.id}
										price={price}
										membership={membership}
										submittingPriceId={submittingPriceId ?? null}
										onSelect={() => handleSelect(price.id)}
										onCancel={handleCancel}
									/>
								))}
							</CallToActionButtons>
						)
					}
				/>
			)}
		</Layout>
	)
}

export default MembershipPage
